// src/posts/gp-dismissing-perimenopause.js

export const meta = {
  slug: 'gp-dismissing-perimenopause',
  title: 'Why your GP keeps dismissing your perimenopause symptoms — and what to do about it',
  date: '2026-05-27',
  readTime: '4 min read',
  description: 'You know something is wrong. You have tried to explain it. And you have been sent home with antidepressants or told it is stress. You are not alone — and there is something you can do about it.',
};

export const content = `
Why your GP keeps dismissing your perimenopause symptoms — and what to do about it

You sat in that waiting room working up the courage to finally say it out loud. Something is wrong. You are not yourself. Your body is doing things you don't understand and your mind feels like it belongs to someone else.

And then you walked out with a prescription for antidepressants and a suggestion to try managing your stress better.

The rage. The hopelessness. The feeling that you are already going mad — and now the one person who was supposed to help you has confirmed your worst fear, that it's all in your head.

It is not in your head. And you are not alone.

Why does this keep happening?

There are a few reasons why GPs get this wrong, and understanding them can help you go back better prepared.

Time is the first problem. A ten minute appointment is not enough to unpick months of confusing, overlapping symptoms. You walk in with a fog in your head, you're stressed and upset, and the words that seemed so clear at home disappear the moment you sit down. You forget half of what you meant to say. The appointment ends before you've really started.

Age assumptions are the second problem. Perimenopause can begin in the mid-30s, but many GPs still associate it with women in their late 40s and 50s. If you're younger, your symptoms get attributed to something else first. Stress. Anxiety. Depression. Life.

And so the prescription pad comes out. Antidepressants are offered not because your GP doesn't care, but because the symptoms you're describing — low mood, anxiety, poor sleep, fatigue — look like depression on paper. Without a full picture of everything you're experiencing and how it all connects, it's an easy mistake to make.

The problem isn't you. The problem is the evidence.

Here's the thing about perimenopause symptoms — they don't arrive in a neat, obvious package. They're scattered across days and weeks. They shift and change. They connect to each other in ways that are almost impossible to see in the moment.

Your GP is making a decision based on a ten minute snapshot. You need to give them the full picture.

Walking in unprepared means walking in with foggy recall, scattered thoughts, and symptoms that are hard to articulate under pressure. Walking in with real data — a clear summary of your symptoms, how often they occur, how severe they are, and how they connect to each other — changes the entire conversation.

Suddenly you're not a woman who thinks she might be struggling. You're a woman with evidence.

What to do if you've been dismissed

First — don't give up. Being dismissed once, or even multiple times, doesn't mean you won't get the help you need. It means you need a different approach.

Second — start tracking. Every day, even just for a few minutes. Log what you're experiencing, how you're sleeping, your mood, your energy. Do it consistently and patterns will emerge that you couldn't see before.

Third — bring the evidence. When you go back to your GP, bring a clear summary of your symptoms and patterns. Not a list you wrote the night before — a real, documented picture built over weeks. Something that's hard to dismiss.

Fourth — keep going. You know your body. You know something has changed. You deserve to be taken seriously and the right GP, with the right information, will take you seriously.

Start tracking today. Build your record. And keep going — you will get the help you need. 🌿
`;
